<template>
  <!--
    Bottom-sheet customiser backed by GET /v1/services/{token}/menu/{id}.

    The product detail API matches the staff-side POS product info shape,
    so the same Simple-vs-Configurable logic from OffcanvasProductOptions
    applies here:
      • type "Simple"       → use product.price + product.modifiers
      • type "Configurable" → pick an option per attribute, match the
                              variants[].option_ids, then use that
                              variant's price/modifiers/stock.
  -->
  <div class="offcanvas-backdrop fade show" style="z-index: 1054;" @click="onBackdrop" />
  <div
    ref="sheet"
    class="position-fixed bottom-0 start-0 end-0 bg-white shadow-lg sheet"
    style="z-index: 1055; max-height: 90vh; max-height: 90dvh; border-radius: 18px 18px 0 0; overflow-y: auto;"
    :style="sheetStyle"
  >
    <!--
      Sticky header doubles as the drag region: a centred grabber cues
      that the sheet can be dragged up/down, and dragging the header moves
      it with the finger. Release snaps back or, if dragged down far
      enough, dismisses. A guard in onHandleDown keeps the close button tappable.
    -->
    <div
      class="sheet-header position-sticky top-0 bg-white border-bottom p-3 d-flex justify-content-between align-items-start z-3"
      @pointerdown="onHandleDown"
    >
      <span class="drag-handle__bar" />
      <div class="me-3 flex-grow-1" style="min-width: 0;">
        <h5 class="mb-1 fw-bold text-break">{{ $ln(displaySource) || $ln(stub) }}</h5>
        <div v-if="!loading && detail.category" class="small text-muted text-break mb-1">
          {{ $ln(detail, 'category') }}
        </div>
        <div v-if="!loading" class="text-theme fw-bold">
          <span v-if="compareUnitPrice" class="text-decoration-line-through text-muted fw-normal me-2">{{ $storeIndex.formatThaiCurrency(compareUnitPrice) }}</span>
          {{ $storeIndex.formatThaiCurrency(currentUnitPrice) }}
          <span v-if="isOutOfStock" class="badge bg-dark ms-2">{{ $t('Sold out') }}</span>
        </div>
        <button
          v-if="!loading && productDescHtml"
          type="button"
          class="desc-toggle mt-1"
          :aria-expanded="descOpen ? 'true' : 'false'"
          @click="descOpen = !descOpen"
        >
          <i class="fa-solid fa-circle-info" />{{ $t('Details') }}
          <i class="fa-solid fa-chevron-down desc-toggle__chev" :class="{ 'is-open': descOpen }" />
        </button>

        <!-- เนื้อความคำบรรยาย — อยู่ในบล็อกชื่อ/ราคาเดียวกับปุ่ม ปิดไว้เป็นค่าเริ่มต้น
             ตัวเลือกจึงอยู่ตำแหน่งเดิมทุกสินค้า ไม่เลื่อนตามความยาวคำบรรยาย
             v-html ได้เพราะ API sanitize มาแล้ว (Product::safeDescription เหลือแค่
             แท็กจัดข้อความ ไม่มี attribute) -->
        <div
          v-if="productDescHtml"
          v-show="descOpen"
          class="desc-body rich-text mt-2"
          v-html="productDescHtml"
        />
      </div>
      <button class="btn-close flex-shrink-0" @click="$emit('close')" />
    </div>

    <!-- Loading skeleton (mirrors the structure that follows) -->
    <div v-if="loading" class="p-3">
      <div class="d-flex align-items-center gap-3 mb-3">
        <div class="flex-shrink-0 rounded-3 skeleton-shimmer" style="width: 80px; height: 80px;" />
        <div class="flex-grow-1">
          <div class="skeleton-shimmer rounded mb-2" style="height: 18px; width: 60%;" />
          <div class="skeleton-shimmer rounded" style="height: 22px; width: 35%;" />
        </div>
      </div>
      <div class="skeleton-shimmer rounded mb-3" style="height: 16px; width: 40%;" />
      <div class="d-flex flex-wrap gap-2 mb-3">
        <div
          v-for="i in 3" :key="'opt-' + i"
          class="skeleton-shimmer rounded-pill" style="height: 38px; width: 90px;"
        />
      </div>
      <div class="skeleton-shimmer rounded mb-2" style="height: 16px; width: 50%;" />
      <div
        v-for="i in 3" :key="'mod-' + i"
        class="skeleton-shimmer rounded-3 mb-2" style="height: 46px;"
      />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="p-4 text-center">
      <i class="fa-solid fa-circle-exclamation fa-2x text-danger mb-2 opacity-75" />
      <p class="fw-bold mb-1">{{ $t('Failed to load product') }}</p>
      <p class="small text-muted mb-3">{{ error }}</p>
      <button class="btn btn-theme text-white rounded-pill px-4" @click="fetchInfo">
        <i class="fa-solid fa-rotate me-2" />{{ $t('Try again') }}
      </button>
    </div>

    <!-- Content -->
    <template v-else>
      <div class="p-3">
        <!-- รายละเอียดสินค้า — การ์ดโทนอุ่นพร้อมป้ายหัวข้อ ให้อ่านเป็น "คำบรรยายเมนู"
             ไม่ใช่ข้อความหลงมาก่อนหัวข้อตัวเลือก
             v-html ได้เพราะ API sanitize มาแล้ว (Product::safeDescription เหลือแค่
             แท็กจัดข้อความ ไม่มี attribute) ไม่มีข้อมูล = ไม่ขึ้นบล็อกนี้เลย -->
        <!-- Attribute selection (Configurable only) -->
        <div
          v-for="attribute in detail.attributes"
          :key="'attr-' + attribute.id"
          class="mb-3"
        >
          <h6 class="fw-bold mb-2">
            <i class="fa-solid fa-layer-group me-2 text-theme" />
            {{ $t(attribute.name) }}
            <span class="text-danger">*</span>
          </h6>
          <div class="d-flex flex-wrap gap-2">
            <template
              v-for="option in attribute.options"
              :key="'opt-' + option.id"
            >
              <button
                v-if="isAttributeOptionAvailable(attribute.id, option.id)"
                type="button"
                class="btn rounded-pill px-3 py-2 fw-semibold"
                :class="{
                  'btn-theme text-white': attributeSelections[attribute.id] === option.id,
                  'btn-outline-secondary': attributeSelections[attribute.id] !== option.id
                }"
                @click="selectAttributeOption(attribute.id, option.id)"
              >
                {{ $t(option.name) }}
              </button>
            </template>
          </div>
        </div>

        <!-- Modifier groups (from variant when Configurable, else from product) -->
        <div
          v-for="modifier in activeModifiers"
          :key="'mod-' + modifier.id"
          class="mb-3"
          :class="{ 'opacity-50': isOutOfStock }"
        >
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="fw-bold mb-0">
              <i class="fa-solid fa-sliders me-2 text-theme" />
              {{ $t(modifier.name) }}
              <span v-if="modifier.is_required" class="text-danger">*</span>
            </h6>
            <span class="small text-muted">
              <template v-if="modifier.allow_multiple && modifier.max_options > 0">
                {{ $t('Select') + ' ' + modifier.min_options + '-' + modifier.max_options }}
              </template>
              <template v-else-if="!modifier.allow_multiple">
                {{ $t('Choose one') }}
              </template>
            </span>
          </div>

          <!-- Single select -->
          <template v-if="!modifier.allow_multiple">
            <label
              v-for="option in modifier.options"
              :key="'opt-' + option.id"
              class="mod-option rounded-3 px-3 py-2 mb-2 d-flex align-items-center gap-2"
              :class="{
                'mod-option-selected': modifierSelections[modifier.id] === option.id,
                'opacity-50': !option.is_in_stock || option.is_ingredient_insufficient
              }"
            >
              <input
                type="radio"
                class="form-check-input m-0"
                :name="'modifier-' + modifier.id"
                :value="option.id"
                v-model="modifierSelections[modifier.id]"
                :disabled="isOutOfStock || !option.is_in_stock || option.is_ingredient_insufficient"
              >
              <span class="flex-grow-1">
                {{ $t(option.name) }}
                <span v-if="!option.is_in_stock || option.is_ingredient_insufficient" class="badge bg-dark ms-1" style="font-size: 0.65rem;">
                  {{ $t('Sold out') }}
                </span>
              </span>
              <span v-if="parseFloat(option.price_adjustment) !== 0" class="text-muted small">
                +{{ $storeIndex.formatThaiCurrency(option.price_adjustment) }}
              </span>
            </label>
          </template>

          <!-- Multi select -->
          <template v-else>
            <label
              v-for="option in modifier.options"
              :key="'opt-' + option.id"
              class="mod-option rounded-3 px-3 py-2 mb-2 d-flex align-items-center gap-2"
              :class="{
                'mod-option-selected': (modifierSelections[modifier.id] || []).includes(option.id),
                'opacity-50': !option.is_in_stock || option.is_ingredient_insufficient
              }"
            >
              <input
                type="checkbox"
                class="form-check-input m-0"
                :value="option.id"
                :checked="(modifierSelections[modifier.id] || []).includes(option.id)"
                @change="toggleMultiOption(modifier, option.id)"
                :disabled="
                  isOutOfStock
                  || !option.is_in_stock
                  || option.is_ingredient_insufficient
                  || (modifier.max_options > 0
                    && (modifierSelections[modifier.id] || []).length >= modifier.max_options
                    && !(modifierSelections[modifier.id] || []).includes(option.id))
                "
              >
              <span class="flex-grow-1">
                {{ $t(option.name) }}
                <span v-if="!option.is_in_stock || option.is_ingredient_insufficient" class="badge bg-dark ms-1" style="font-size: 0.65rem;">
                  {{ $t('Sold out') }}
                </span>
              </span>
              <span v-if="parseFloat(option.price_adjustment) !== 0" class="text-muted small">
                +{{ $storeIndex.formatThaiCurrency(option.price_adjustment) }}
              </span>
            </label>
          </template>
        </div>

        <!-- Remark -->
        <div class="mb-3">
          <h6 class="fw-bold mb-2">
            <i class="fa-solid fa-comment-dots me-2 text-theme" />
            {{ $t('Note for kitchen') }}
          </h6>
          <textarea
            v-model="remark"
            class="form-control"
            rows="2"
            :placeholder="$t('e.g. no peanuts, less spicy')"
            :disabled="isOutOfStock"
          />
        </div>
      </div>

      <!--
        Sticky action footer — keeps Quantity + Add to cart visible on
        small phone screens where the option list exceeds the 90vh sheet
        height and would otherwise push the primary CTA below the fold.
      -->
      <div class="position-sticky bottom-0 bg-white border-top p-3 z-3">
        <div class="d-flex align-items-center gap-2">
          <div
            class="qty-pill d-inline-flex align-items-center"
            :class="{ 'opacity-50': isOutOfStock }"
          >
            <button
              type="button"
              class="qty-btn"
              :aria-label="$t('Decrease')"
              :disabled="isOutOfStock || quantity <= 1"
              @click="quantity = Math.max(1, quantity - 1)"
            >
              <i class="fa-solid fa-minus" />
            </button>
            <span class="qty-value">{{ quantity }}</span>
            <button
              type="button"
              class="qty-btn"
              :aria-label="$t('Increase')"
              :disabled="isOutOfStock"
              @click="quantity++"
            >
              <i class="fa-solid fa-plus" />
            </button>
          </div>
          <button
            class="btn btn-theme text-white flex-grow-1 fw-bold py-2 rounded-pill d-flex justify-content-center align-items-center gap-2"
            :disabled="!canAdd"
            @click="submit"
          >
            <i class="fa-solid" :class="editItem ? 'fa-check' : 'fa-cart-plus'" />
            <span>{{ editItem ? $t('Update') : $t('Add to Cart') }}</span>
            <span class="badge bg-white bg-opacity-25 fw-bold ms-1">
              {{ $storeIndex.formatThaiCurrency(totalPrice) }}
            </span>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ServiceMenu } from '@/services/endpoint/services.service';
import { useScrollRestore } from '@/composables/useScrollRestore';
import { pickLocalized } from '@/utils/i18nName';

export default {
  name: 'ProductOptionsSheet',
  props: {
    // `product` is the menu-list row (lightweight) — full detail comes
    // from the info API. We only use it for the immediate header label
    // and to know which id to fetch.
    product: { type: Object, required: true },
    editItem: { type: Object, default: null },
  },
  emits: ['close', 'add', 'update'],
  data() {
    return {
      loading: true,
      error: '',
      descOpen: false,   // คำบรรยายยาว: ปิดไว้ก่อน ลูกค้าที่อยากอ่านค่อยกดเปิด
      // Skeleton label fallback while detail is loading.
      stub: { name: this.product.name, name_en: this.product.name_en },
      // Full product detail from /menu/{id}.
      detail: {
        id: null,
        name: '',
        name_en: '',
        type: 'Simple',
        price: '0.00',
        image_url: null,
        thumbnail_url: null,
        category: '',
        category_en: '',
        is_in_stock: true,
        is_ingredient_insufficient: false,
        attributes: [],
        variants: [],
        modifiers: [],
      },
      // Form state
      attributeSelections: {},     // { [attributeId]: optionId }
      modifierSelections: {},      // { [modifierId]: optionId | optionId[] }
      quantity: 1,
      remark: '',

      // --- Drag-to-dismiss ----------------------------------------------
      // dragOffset is the sheet's translateY in px: 0 = open, larger =
      // dragged down. On release it snaps back or, past a threshold,
      // slides out and emits `close`. dragging=true drops the transition
      // so the sheet tracks the finger 1:1.
      dragging: false,
      dragOffset: 0,
      _wrapH: 0,
      _dragStartY: 0,
      _dragStartOffset: 0,
      _onDragMove: null,
      _onDragEnd: null,
      _onResizeWrap: null,
    };
  },
  computed: {
    token() {
      return this.$route?.params?.token;
    },
    /**
     * Selected variant only matters for Configurable products. It's the
     * one whose option_ids match the user's full attribute selection.
     */
    selectedVariant() {
      if (!this.detail.variants?.length || !this.detail.attributes?.length) return null;
      const selectedOptionIds = Object.values(this.attributeSelections);
      if (selectedOptionIds.length !== this.detail.attributes.length) return null;
      return this.detail.variants.find(v => {
        if (!v.option_ids || v.option_ids.length !== selectedOptionIds.length) return false;
        return selectedOptionIds.every(id => v.option_ids.includes(id));
      }) || null;
    },
    // คืน object ไม่ใช่ string เพื่อให้ $ln เลือกภาษาได้ (ต้องเห็นทั้ง name และ name_en)
    displaySource() {
      return this.selectedVariant ? this.selectedVariant : this.detail;
    },
    /**
     * รายละเอียดสินค้า (HTML ที่ API sanitize มาแล้ว) — variant ที่เลือกมีคำบรรยาย
     * ของตัวเองก็ใช้ตัวนั้น
     * ใช้ pickLocalized ตรง ๆ (ไม่ผ่าน $ln) เพราะ $ln ส่งค่าเข้า $t อีกชั้น ซึ่งเหมาะกับ
     * "ชื่อ" ที่อาจมีคีย์ในพจนานุกรม แต่ประโยคบรรยายไม่มีทางมีคีย์อยู่แล้ว
     */
    productDescHtml() {
      const source = this.selectedVariant && this.selectedVariant.description
        ? this.selectedVariant
        : this.detail;
      return pickLocalized(source, this.$i18n.locale, 'description');
    },
    /**
     * Modifier source: variant-level when Configurable + a variant is picked,
     * otherwise product-level (Simple type or before a variant lands).
     */
    activeModifiers() {
      if (this.selectedVariant) return this.selectedVariant.modifiers || [];
      return this.detail.modifiers || [];
    },
    // item_discount: ราคาหลังลด (BE คำนวณ) ของ variant/สินค้าแม่ — null = ไม่มีโปร
    basePromotionPrice() {
      const src = this.selectedVariant ? this.selectedVariant : this.detail;
      const pp = src?.promotion_price;
      return (pp !== null && pp !== undefined && pp !== '') ? parseFloat(pp) : null;
    },
    baseOriginalPrice() {
      const src = this.selectedVariant ? this.selectedVariant : this.detail;
      return parseFloat(src?.price) || 0;
    },
    basePrice() {
      return this.basePromotionPrice != null ? this.basePromotionPrice : this.baseOriginalPrice;
    },
    // ราคาเต็ม (ขีดฆ่า) — โปรมาก่อน (โชว์ราคาปกติ) ไม่งั้นใช้ compare_at_price ถ้าสูงกว่าจริง
    baseCompareAtPrice() {
      if (this.basePromotionPrice != null) return this.baseOriginalPrice;
      const src = this.selectedVariant ? this.selectedVariant : this.detail;
      const compare = Number(src?.compare_at_price);
      return !isNaN(compare) && compare > this.basePrice ? compare : 0;
    },
    compareUnitPrice() {
      return this.baseCompareAtPrice ? this.baseCompareAtPrice + this.modifierPriceAdjustment : 0;
    },
    modifierPriceAdjustment() {
      let adj = 0;
      for (const modifier of this.activeModifiers) {
        const sel = this.modifierSelections[modifier.id];
        if (!sel && sel !== 0) continue;
        if (modifier.allow_multiple && Array.isArray(sel)) {
          for (const optId of sel) {
            const opt = modifier.options.find(o => o.id === optId);
            if (opt) adj += parseFloat(opt.price_adjustment) || 0;
          }
        } else {
          const opt = modifier.options.find(o => o.id === sel);
          if (opt) adj += parseFloat(opt.price_adjustment) || 0;
        }
      }
      return adj;
    },
    currentUnitPrice() {
      return this.basePrice + this.modifierPriceAdjustment;
    },
    totalPrice() {
      return this.currentUnitPrice * this.quantity;
    },
    isOutOfStock() {
      // For Configurable: gate on the picked variant; for Simple: gate on
      // the product-level stock flags.
      if (this.detail.attributes?.length > 0) {
        if (!this.selectedVariant) return false; // nothing picked yet
        return !this.selectedVariant.is_in_stock || this.selectedVariant.is_ingredient_insufficient;
      }
      return !this.detail.is_in_stock || this.detail.is_ingredient_insufficient;
    },
    canAdd() {
      if (this.loading || this.error) return false;
      if (this.isOutOfStock) return false;
      // Configurable products require a complete variant selection.
      if (this.detail.attributes?.length > 0 && !this.selectedVariant) return false;
      // Required modifier groups must be satisfied within min/max bounds.
      for (const modifier of this.activeModifiers) {
        if (!modifier.is_required) continue;
        const sel = this.modifierSelections[modifier.id];
        if (modifier.allow_multiple) {
          if (!Array.isArray(sel) || !sel.length) return false;
          if (modifier.min_options > 0 && sel.length < modifier.min_options) return false;
        } else {
          if (!sel) return false;
        }
      }
      return true;
    },
    /**
     * Sheet transform. While dragging we drop the transition so it tracks
     * the finger; on release the spring-ish cubic-bezier eases it back to
     * open (or off-screen on dismiss).
     */
    sheetStyle() {
      return {
        transform: `translateY(${this.dragOffset}px)`,
        transition: this.dragging
          ? 'none'
          : 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
      };
    },
  },
  watch: {
    selectedVariant(newVal, oldVal) {
      // When the variant changes, reset modifier selections and seed
      // defaults — variant-scoped modifiers may be entirely different.
      if (newVal?.id !== oldVal?.id) {
        this.modifierSelections = {};
        if (!this.editItem) {
          this.$nextTick(() => this.initDefaultModifiers());
        }
      }
    },
  },
  mounted() {
    this.fetchInfo();
    useScrollRestore(() => this.$refs.sheet);

    this.$nextTick(() => this.measureWrap());
    this._onResizeWrap = () => this.measureWrap();
    window.addEventListener('resize', this._onResizeWrap);
  },
  beforeUnmount() {
    if (this._onResizeWrap) window.removeEventListener('resize', this._onResizeWrap);
    this.detachDragListeners();
  },
  methods: {
    onBackdrop() {
      this.$emit('close');
    },

    // --- Drag-to-dismiss ------------------------------------------------
    /** Cache the sheet's rendered height — the dismiss threshold is relative to it. */
    measureWrap() {
      const s = this.$refs.sheet;
      this._wrapH = s ? s.offsetHeight : Math.round(window.innerHeight * 0.9);
    },
    onHandleDown(e) {
      // Tapping the close button / details toggle must still fire, and the
      // expanded description must scroll — don't hijack any of them as a drag.
      if (e.target.closest('.btn-close, .desc-toggle, .desc-body')) return;
      this.measureWrap();
      this.dragging = true;
      this._dragStartY = e.clientY;
      this._dragStartOffset = this.dragOffset;
      this._onDragMove = ev => this.onDragMove(ev);
      this._onDragEnd = () => this.onDragEnd();
      window.addEventListener('pointermove', this._onDragMove, { passive: false });
      window.addEventListener('pointerup', this._onDragEnd);
      window.addEventListener('pointercancel', this._onDragEnd);
      e.preventDefault();
    },
    onDragMove(e) {
      let next = this._dragStartOffset + (e.clientY - this._dragStartY);
      // Rubber-band when pulled above the open position so it resists
      // rather than floating up off its bottom anchor.
      if (next < 0) next *= 0.3;
      this.dragOffset = next;
      if (e.cancelable) e.preventDefault();
    },
    onDragEnd() {
      this.detachDragListeners();
      this.dragging = false;
      const h = this._wrapH || window.innerHeight;
      // Dragged down past ~30% of the sheet height → dismiss, else snap back.
      if (this.dragOffset > h * 0.3) this.dismiss();
      else this.dragOffset = 0;
    },
    detachDragListeners() {
      if (this._onDragMove) window.removeEventListener('pointermove', this._onDragMove);
      if (this._onDragEnd) {
        window.removeEventListener('pointerup', this._onDragEnd);
        window.removeEventListener('pointercancel', this._onDragEnd);
      }
      this._onDragMove = null;
      this._onDragEnd = null;
    },
    /** Slide the sheet out, then let the parent unmount it. */
    dismiss() {
      this.dragOffset = (this._wrapH || window.innerHeight) + 40;
      setTimeout(() => this.$emit('close'), 260);
    },

    async fetchInfo() {
      if (!this.token || !this.product?.id) {
        this.error = this.$t('Missing product or session');
        this.loading = false;
        return;
      }
      this.loading = true;
      this.error = '';
      try {
        const res = await ServiceMenu.info(this.token, this.product.id);
        const data = res.data?.data ?? res.data;
        // Normalise defaults so downstream computeds don't NPE on partial payloads.
        this.detail = {
          attributes: [],
          variants: [],
          modifiers: [],
          ...data,
        };
        if (this.editItem) {
          this.prefillFromEdit();
        } else {
          this.initDefaultAttributes();
          this.$nextTick(() => this.initDefaultModifiers());
        }
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || this.$t('Network error');
      } finally {
        this.loading = false;
      }
    },

    // --- Attribute / variant helpers ------------------------------------
    /**
     * An option is "available" if at least one variant in the catalogue
     * includes it given the user's other selections — so we hide dead
     * combinations rather than letting the user pick a missing variant.
     */
    isAttributeOptionAvailable(attributeId, optionId) {
      const test = { ...this.attributeSelections, [attributeId]: optionId };
      const ids = Object.values(test);
      return this.detail.variants.some(v => ids.every(id => v.option_ids?.includes(id)));
    },
    selectAttributeOption(attributeId, optionId) {
      this.attributeSelections = { ...this.attributeSelections, [attributeId]: optionId };
    },
    initDefaultAttributes() {
      if (!this.detail.attributes?.length || !this.detail.variants?.length) return;
      const first = this.detail.variants.find(v => v.is_in_stock && !v.is_ingredient_insufficient)
        || this.detail.variants[0];
      if (!first?.option_ids) return;
      const sel = {};
      for (const attr of this.detail.attributes) {
        const matched = attr.options.find(o => first.option_ids.includes(o.id));
        if (matched) sel[attr.id] = matched.id;
      }
      this.attributeSelections = sel;
    },

    // --- Modifier helpers ----------------------------------------------
    toggleMultiOption(modifier, optionId) {
      const current = [...(this.modifierSelections[modifier.id] || [])];
      const idx = current.indexOf(optionId);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        if (modifier.max_options > 0 && current.length >= modifier.max_options) return;
        current.push(optionId);
      }
      this.modifierSelections = { ...this.modifierSelections, [modifier.id]: current };
    },
    initDefaultModifiers() {
      const sel = {};
      for (const modifier of this.activeModifiers) {
        const defaults = modifier.options.filter(
          o => o.is_default && o.is_in_stock && !o.is_ingredient_insufficient
        );
        if (!defaults.length) continue;
        if (modifier.allow_multiple) sel[modifier.id] = defaults.map(o => o.id);
        else sel[modifier.id] = defaults[0].id;
      }
      this.modifierSelections = sel;
    },

    // --- Edit prefill ----------------------------------------------------
    prefillFromEdit() {
      const e = this.editItem;
      if (!e) return;
      this.quantity = e.quantity || 1;
      this.remark = e.remark || '';

      // Re-derive attribute selections from variant_id if Configurable.
      if (this.detail.variants?.length > 0 && e.variant_id) {
        const variant = this.detail.variants.find(v => v.id === e.variant_id);
        if (variant?.option_ids) {
          const sel = {};
          for (const attr of this.detail.attributes) {
            const matched = attr.options.find(o => variant.option_ids.includes(o.id));
            if (matched) sel[attr.id] = matched.id;
          }
          this.attributeSelections = sel;
        }
      } else if (!this.detail.variants?.length) {
        // Simple product — nothing variant-related to restore.
      }

      // Wait for variant watcher to settle before applying modifier state.
      this.$nextTick(() => {
        const optionIds = Array.isArray(e.modifier_option_ids) ? e.modifier_option_ids : [];
        const sel = {};
        for (const modifier of this.activeModifiers) {
          const matched = modifier.options.filter(o => optionIds.includes(o.id));
          if (!matched.length) continue;
          if (modifier.allow_multiple) sel[modifier.id] = matched.map(o => o.id);
          else sel[modifier.id] = matched[0].id;
        }
        this.modifierSelections = sel;
      });
    },

    // --- Submit ----------------------------------------------------------
    submit() {
      if (!this.canAdd) return;
      const selectedMods = [];
      const modifierOptionIds = [];
      for (const modifier of this.activeModifiers) {
        const sel = this.modifierSelections[modifier.id];
        if (!sel && sel !== 0) continue;
        const ids = Array.isArray(sel) ? sel : [sel];
        if (!ids.length) continue;
        const options = modifier.options.filter(o => ids.includes(o.id));
        if (!options.length) continue;
        selectedMods.push({
          id: modifier.id,
          name: modifier.name,
          name_en: modifier.name_en,
          options: options.map(o => ({
            id: o.id,
            name: o.name,
            name_en: o.name_en,
            price: parseFloat(o.price_adjustment) || 0,
          })),
        });
        modifierOptionIds.push(...options.map(o => o.id));
      }

      const payload = {
        product_id: this.detail.id,
        variant_id: this.selectedVariant?.id || null,
        name: this.detail.name,
        name_en: this.detail.name_en,
        variant_name: this.selectedVariant?.name || null,
        variant_name_en: this.selectedVariant?.name_en || null,
        unit_price: this.currentUnitPrice,
        quantity: this.quantity,
        remark: this.remark.trim(),
        modifier_option_ids: modifierOptionIds,
        selected_modifiers: selectedMods,
      };
      this.$emit(this.editItem ? 'update' : 'add', payload);
    },
  },
};
</script>

<style scoped>
/* --- Drag header / grabber --------------------------------------------
   The sticky header is the drag region: grab cursor + touch-action:none
   so a drag here moves the sheet instead of scrolling content. The close
   button re-enables its own pointer cursor. The grabber bar is a centred
   cue at the very top, sitting inside the header's top padding. */
.sheet-header {
  /* position:sticky (Bootstrap .position-sticky) already establishes the
     containing block for the absolutely-positioned grabber — don't
     override it with position:relative or the header stops sticking. */
  cursor: grab;
  touch-action: none;
}
.sheet-header:active { cursor: grabbing; }
.sheet-header .btn-close { cursor: pointer; }

/* ปุ่มอ่านคำบรรยาย — ลิงก์เล็ก ๆ ใต้ชื่อ/ราคา ไม่ใช่กล่อง เพื่อไม่ให้แย่งสายตา
   ไปจากตัวเลือกซึ่งเป็นงานหลักของแผ่นนี้ */
.desc-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #d46010;
  font-size: 0.78rem;
  font-weight: 600;
}
.desc-toggle:focus,
.desc-toggle:focus-visible { outline: none; box-shadow: none; }
.desc-toggle__chev {
  font-size: 0.65rem;
  transition: transform 0.2s ease;
}
.desc-toggle__chev.is-open { transform: rotate(180deg); }
/* เนื้อความกางแล้ว — อยู่ในหัวสินค้าซึ่ง sticky จึงจำกัดความสูงไว้ 35vh กันคำบรรยาย
   ยาว ๆ ดันให้แถบหัวสูงจนบังจอ (เกินกว่านั้นเลื่อนอ่านในกล่องได้)
   touch-action: pan-y ปลดล็อกการเลื่อนด้วยนิ้วในกล่องนี้ เพราะหัวแผ่นตั้ง
   touch-action: none ไว้เพื่อใช้ลากปิด — ดู guard ใน onHandleDown ประกอบ */
.desc-body {
  max-height: 35vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
}
/* เนื้อหาที่มาจาก editor หลังบ้าน (ย่อหน้า/ตัวเน้น/ลิสต์เก่า) — คุมระยะให้พอดี
   กับการ์ด ไม่ใช้ margin ก้อนใหญ่แบบ default ของเบราว์เซอร์
   line-height 1.85 เผื่อวรรณยุกต์/สระไทยไม่ให้บรรทัดแน่นเกินไป */
.rich-text {
  color: #5f6673;
  font-size: 0.875rem;
  line-height: 1.85;
}
.rich-text :deep(p) { margin-bottom: 0.35rem; }
.rich-text :deep(p:last-child) { margin-bottom: 0; }
.rich-text :deep(ul),
.rich-text :deep(ol) { margin-bottom: 0.35rem; padding-left: 1.15rem; }
.rich-text :deep(ul:last-child),
.rich-text :deep(ol:last-child) { margin-bottom: 0; }
.rich-text :deep(li) { margin-bottom: 0.1rem; }
.rich-text :deep(strong),
.rich-text :deep(b) { color: #343a40; font-weight: 700; }
.rich-text :deep(u) { text-decoration-color: rgba(233, 108, 21, 0.6); }
.drag-handle__bar {
  position: absolute;
  top: 6px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 4px;
  border-radius: 999px;
  background: #d0d3d7;
}

.skeleton-shimmer {
  background: linear-gradient(90deg, #eceff2 0%, #f5f7f9 50%, #eceff2 100%);
  background-size: 200% 100%;
  animation: pos-skeleton-shimmer 1.25s ease-in-out infinite;
  display: block;
}
@keyframes pos-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/*
  Quantity stepper — duplicated from TabMenu.vue so the look is identical
  across cart and customisation sheet. Scoped styles don't cross
  components, so it has to live here too.
*/
.qty-pill {
  background: rgba(233, 108, 21, 0.08);
  border: 1px solid rgba(233, 108, 21, 0.25);
  border-radius: 999px;
  padding: 2px;
}
.qty-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  color: #d46010;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  transition: background-color 0.15s;
}
.qty-btn:hover:not(:disabled) {
  background-color: rgba(233, 108, 21, 0.15);
}
.qty-btn:active:not(:disabled) {
  background-color: rgba(233, 108, 21, 0.25);
}
.qty-btn:disabled {
  color: #b8b8b8;
  cursor: not-allowed;
}
.qty-value {
  min-width: 36px;
  text-align: center;
  font-weight: 700;
  color: #333;
  font-size: 1rem;
}

/*
  Modifier option row. Default state is a subtle outlined row; selected
  state lights up with the theme color on both border and background so
  it's unambiguous which options the customer has picked.
*/
.mod-option {
  cursor: pointer;
  border: 1px solid #dee2e6;
  background: #fff;
  transition: border-color 0.15s, background-color 0.15s;
}
.mod-option:hover {
  border-color: #ffb380;
}
.mod-option-selected {
  border-color: #E96C15 !important;
  background: rgba(233, 108, 21, 0.08);
}
</style>
